import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, takeUntil } from 'rxjs';

import { ToastService } from '@services/common/toast.service';
import { UserState, UserStatus } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';
import { UserHttpService } from '@services/datasources/user-http.service';

export type FormulaireCallCompletion = 'userSuccess' | 'userFail' | 'passSuccess' | 'passFail';

@Component({
    selector: 'app-settings-form-user',
    templateUrl: './settings-form-user.component.html',
    styleUrls: ['./settings-form-user.component.scss'],
})
export class SettingsFormUserComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$: Observable<UserClaims | undefined>;
    @Select(UserState.getUserStatus) userStatus$: Observable<UserStatus | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$: Observable<QuestionnaireDto>;
    @Input() isOnboarding: boolean = false;
    @Output() onboardedEvent: EventEmitter<any> = new EventEmitter();

    public settingsForm!: FormGroup;

    private userStatus: UserStatus;
    private ngUnsubscribe = new Subject<void>();
    public userClaim: UserClaims;
    public questionnaire: QuestionnaireDto;
    public loading: boolean = false;

    public isAdminForm: boolean = false;

    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private userService: UserHttpService,
    ) {
        this.isAdminForm =
            this.featureService.hasUiFeatures(['organization_admin'], false) || this.featureService.hasSudoFeature();
        this.settingsForm = this.formBuilder.group({
            userGroup: this.formBuilder.group({
                lastname: new FormControl('', Validators.required),
                firstname: new FormControl('', Validators.required),
                role: new FormControl(''),
                email: new FormControl({ value: '', disabled: true }, Validators.required),
                phoneNumber: new FormControl('', CustomValidators.phoneNumberValidator),
            }),
            securityGroup: this.formBuilder.group(
                {
                    oldPassword: new FormControl('', Validators.required),
                    newPassword: new FormControl('', [Validators.required, CustomValidators.passwordValidator]),
                    confirmPassword: new FormControl('', Validators.required),
                },
                {
                    validators: [
                        CustomValidators.mustNotMatch('oldPassword', 'newPassword'),
                        CustomValidators.mustMatch('newPassword', 'confirmPassword'),
                    ],
                },
            ),
        });
    }

    get userFormGroup(): FormGroup {
        return this.settingsForm.get('userGroup') as FormGroup;
    }

    get securityFormGroup(): FormGroup {
        return this.settingsForm.get('securityGroup') as FormGroup;
    }

    ngOnInit(): void {
        this.userStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            this.userStatus = status ?? UserStatus.NONE;
        });

        this.authService.authenticatedUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (aU) => {
                const userFormGroup = this.settingsForm.get('userGroup')!;
                if (aU) {
                    let idToken = aU.getSignInUserSession()!.getIdToken();
                    userFormGroup
                        .get('lastname')!
                        .setValue('family_name' in idToken.payload ? idToken.payload['family_name'] : '');
                    userFormGroup
                        .get('firstname')!
                        .setValue('given_name' in idToken.payload ? idToken.payload['given_name'] : '');
                    userFormGroup
                        .get('role')!
                        .setValue('custom:role' in idToken.payload ? idToken.payload['custom:role'] : '');
                    userFormGroup.get('email')!.setValue('email' in idToken.payload ? idToken.payload['email'] : '');
                    userFormGroup
                        .get('phoneNumber')!
                        .setValue('phone_number' in idToken.payload ? idToken.payload['phone_number'] : '');
                } else {
                    userFormGroup.disable();
                }
            },
            error: (error) => {},
            complete: () => {},
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        const userFormGroup = this.settingsForm.get('userGroup')!;
        const securityFormGroup = this.settingsForm.get('securityGroup')!;

        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        let completionSignals: Subject<FormulaireCallCompletion>[] = [];
        let userInfoUpdateResult: Subject<FormulaireCallCompletion>;
        let passUpdateResult: Subject<FormulaireCallCompletion>;

        if (userFormGroup.valid && (!userFormGroup.pristine || (userFormGroup.pristine && this.isOnboarding))) {
            userInfoUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(userInfoUpdateResult);

            const user: UserInfoDto = {
                email: this.userFormGroup.get('email')?.value,
                phone_number: this.userFormGroup.get('phoneNumber')?.value,
                role: this.userFormGroup.get('role')?.value,
                firstname: this.userFormGroup.get('firstname')?.value,
                lastname: this.userFormGroup.get('lastname')?.value,
                roles: [],
            };

            this.userService.updateCognitoUser(user).subscribe({
                next: () => {
                    userInfoUpdateResult.next('userSuccess');
                    userInfoUpdateResult.complete();
                    userFormGroup.markAsPristine();
                    this.toastService.showSuccess({
                        details: 'components.settingsForm.userSection.toasts.updateSuccess',
                    });
                },
                error(err) {
                    userInfoUpdateResult.next('userFail');
                    userInfoUpdateResult.complete();
                    this.toastService.showError({
                        details: 'components.settingsForm.userSection.toasts.updateError',
                    });
                },
            });
        }
        if (securityFormGroup.valid && !securityFormGroup.pristine) {
            passUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(passUpdateResult);
            this.authService
                .changePassword(
                    securityFormGroup.get('oldPassword')?.value,
                    securityFormGroup.get('newPassword')?.value,
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: (message) => {
                        securityFormGroup.markAsPristine();
                        passUpdateResult.next('passSuccess');
                        passUpdateResult.complete();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.securitySection.toasts.updateSuccess',
                        });
                    },
                    error: (message) => {
                        passUpdateResult.next('passFail');
                        passUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.securitySection.toasts.updateError',
                        });
                    },
                });
        }

        /**
         * all the api calls emit a completion signal with a type allowing to know what has success/failed errors and toast can be called before but need to know if everything succeded before navigation.
         * it's also possible to handle success/error from this callback
         *  if (completionSignals.length !== 0) {
         *      this.loading = true;
         *  }
         */
        combineLatest(completionSignals)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (callbacks) => {
                    if (
                        this.isOnboarding &&
                        callbacks.includes('userSuccess') &&
                        this.userStatus === UserStatus.ONBOARDED
                    ) {
                        this.loading = false;
                        this.onboardedEvent.emit(true);
                    }

                    this.loading = false;
                },
            });
    }

    // sometimes even if the securityform is not valid because it's empty, it is possible to submit
    public customFormValidity(): boolean {
        return this.isAdminForm
            ? this.userFormGroup.valid &&
                  (this.securityFormGroup.pristine || this.securityFormGroup.valid) &&
                  (!this.userFormGroup.pristine || !this.securityFormGroup.pristine)
            : this.userFormGroup.valid &&
                  (this.securityFormGroup.pristine || this.securityFormGroup.valid) &&
                  (!this.userFormGroup.pristine || !this.securityFormGroup.pristine);
    }
}
